import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, FileText, ArrowsClockwise, Wallet, CheckCircle } from "@phosphor-icons/react";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
      {/* background glows */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-emerald-200/50 blur-3xl" />
        <div className="absolute top-20 right-[-160px] w-[560px] h-[560px] rounded-full bg-sky-200/60 blur-3xl" />
        <div className="absolute inset-0 bg-grid opacity-[0.35] mask-radial" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-700 animate-pulse" />
              Currently onboarding founding customers
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              data-testid="hero-heading"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-slate-900 leading-[1.02]"
            >
              Unlock cash flow <br className="hidden sm:block" />
              without waiting for <span className="text-gradient-blue">invoice payments</span>.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              data-testid="hero-subheading"
              className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed"
            >
              Transform unpaid invoices into working capital and keep your business moving forward.
              Skip the 30–90 day wait — get funded on your schedule.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3"
            >
              <a href="#early-access" data-testid="hero-primary-cta"
                 className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-700 to-emerald-800 px-6 py-3.5 text-sm font-semibold text-white shadow-sm hover:shadow-glow-blue hover:-translate-y-0.5 transition-all">
                Join Early Access
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a href="#how-it-works" data-testid="hero-secondary-cta"
                 className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-6 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-all">
                <PlayCircle size={18} weight="duotone" />
                How It Works
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500"
            >
              <span className="inline-flex items-center gap-1.5"><CheckCircle weight="fill" className="text-emerald-700" size={14} /> 100% digital process</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle weight="fill" className="text-emerald-700" size={14} /> No collateral required</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle weight="fill" className="text-emerald-700" size={14} /> Built for MSMEs & growing businesses</span>
            </motion.div>
          </div>

          {/* Illustration */}
          <div className="lg:col-span-5 relative">
            <HeroIllustration />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroIllustration() {
  return (
    <div data-testid="hero-illustration" className="relative h-[440px] md:h-[520px] w-full">
      {/* Wallet card - back */}
      <motion.div
        initial={{ opacity: 0, x: 60, y: 40 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="absolute right-2 top-12 w-72 rounded-3xl glass p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-600">
            <Wallet size={18} weight="duotone" />
            <span className="text-xs font-semibold uppercase tracking-widest">Working Capital</span>
          </div>
          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">LIVE</span>
        </div>
        <div className="mt-6">
          <p className="text-xs text-slate-500">Available balance</p>
          <motion.p
            key="bal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="mt-1 font-display text-3xl font-semibold text-slate-900"
          >
            ₹ 8,42,500
          </motion.p>
          <p className="mt-1 text-xs text-emerald-600 font-semibold">▲ Funded in 36 hours</p>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-2">
          {[42, 68, 90, 74, 88, 96].slice(0, 6).map((h, i) => (
            <div key={i} className="h-10 rounded-md bg-emerald-100/70 overflow-hidden flex items-end">
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{ delay: 1 + i * 0.08, duration: 0.6 }}
                className="w-full bg-gradient-to-t from-emerald-700 to-emerald-400 rounded-md"
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* Invoice card - front */}
      <motion.div
        initial={{ opacity: 0, x: -20, y: 20, rotate: -6 }}
        animate={{ opacity: 1, x: 0, y: 0, rotate: -3 }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="absolute left-2 top-40 w-64 rounded-3xl bg-white shadow-glow-blue border border-slate-100 p-6"
      >
        <div className="flex items-center gap-2 text-slate-600">
          <FileText size={18} weight="duotone" className="text-emerald-700" />
          <span className="text-xs font-semibold uppercase tracking-widest">Invoice #INV-2087</span>
        </div>
        <p className="mt-4 text-xs text-slate-500">Due in 62 days</p>
        <p className="mt-1 font-display text-2xl font-semibold text-slate-900">₹ 10,00,000</p>
        <div className="mt-4 border-t border-dashed border-slate-200 pt-3">
          <p className="text-[11px] text-slate-500">Buyer</p>
          <p className="text-sm font-medium text-slate-800">Northwind Traders Pvt. Ltd.</p>
        </div>
      </motion.div>

      {/* Animated arrow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
          className="grid place-items-center w-14 h-14 rounded-full bg-white shadow-glow-blue border border-emerald-100"
        >
          <ArrowsClockwise size={22} weight="bold" className="text-emerald-700" />
        </motion.div>
      </motion.div>

      {/* Floating chips */}
      <motion.div
        className="absolute bottom-6 left-6 rounded-2xl glass px-4 py-3 flex items-center gap-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
      >
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-xs font-semibold text-slate-700">Funded · Zero collateral</span>
      </motion.div>
      <motion.div
        className="absolute top-2 right-6 rounded-2xl glass px-4 py-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
      >
        <p className="text-[10px] uppercase tracking-widest text-slate-500">Approval time</p>
        <p className="text-sm font-semibold text-slate-800">24–48 hours</p>
      </motion.div>
    </div>
  );
}
